// @ts-strict-ignore
import { ProjectGroupVO } from '@project/model/vo/project-group.vo';
import { ProjectNavigationInterface } from './project-navigation.interface';
import { Dictionary } from 'typescript-collections';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { ProjectHeadVO, ProjectVO } from '@project/model/vo/project.vo';
import { ProjectModel } from '@project/model/project.model';
import { Subject } from 'rxjs';
import { ProjectGroupUtils } from '@project/components/project/util/project-group-utils';
import { UserVO } from '@user/model/vo/user.vo';
import { uniqWith } from 'lodash';
import { StringUtils } from '@shared/util/string-utils';
import { UserUtils } from '@user/service/user-utils';
import { CloneUtils } from '@shared/util/clone-utils';

export class ProjectNavigationModel {
  public static readonly ROOT_NAME: string = 'Home';
  public static readonly ROOT_ID: number = -999;
  public static readonly ROOT_USER_ID: number = -998;

  public static readonly ROOT_NAME_MY_PROJECTS: string = 'projectNavigation.my_projects';
  public static readonly ROOT_NAME_HIDDEN: string = 'projectNavigation.hidden_projects';
  public static readonly ROOT_NAME_MY_ASSIGNED: string = 'projectNavigation.my_assigned_projects';
  public static readonly ROOT_NAME_RECENTLY_USED: string = 'projectNavigation.recently_used_projects';
  public static readonly ROOT_NAME_PINNED: string = 'projectNavigation.pinned_projects';
  public static readonly ROOT_NAME_SHARED: string = 'projectNavigation.shared_projects';

  public static readonly MAX_SEEN_SHARED_USERS_AMOUNT = 100;

  public isInit: boolean;

  public compositeCheckBoxValue: boolean;
  public canNavigateIntoProjectGroups: boolean = true;

  public rootProjectGroup: ProjectGroupVO; // all data
  public rootSearchProjectGroup: ProjectGroupVO; // all found data
  private _currentProjectFolder: ProjectGroupVO = null; // data to render, contains deep copy of the latest element in breadcrumbs with filtered result

  private _currentViewedProjectGroup: ProjectGroupVO = undefined; // represents the project with its configurations currently viewed in projectNavigation

  public currentModel: ProjectNavigationInterface;

  public currentFilterItem: FilterItem;
  public visiblePinnedProjects: boolean = false;
  public toggleExtendedSearch: boolean = false;

  public currentTab: ProjectNavigationTab;

  public openedFromDashboard: boolean = false;

  public navigationChain: ProjectGroupVO[];

  public searchMode: boolean;
  public searchText: string;

  public parentGroupMap: Dictionary<number, ProjectGroupVO> = new Dictionary<number, ProjectGroupVO>();
  public pinnedGroupsParentGroupMap: Dictionary<number, ProjectGroupVO> = new Dictionary<number, ProjectGroupVO>();
  public recentlyUsedGroupsParentGroupMap: Dictionary<number, ProjectGroupVO> = new Dictionary<number, ProjectGroupVO>();

  public currentChanged = new BehaviorSubject<ProjectGroupVO>(this.currentProjectFolder);

  public selectedProjectGroups: Dictionary<number, ProjectGroupVO> = new Dictionary<number, ProjectGroupVO>();
  public selectedProjects: Dictionary<number, ProjectHeadVO> = new Dictionary<number, ProjectHeadVO>();
  public allSharedUsers: UserIdName[] = [];
  public visibleSharedUsers: UserIdName[] = [];

  public isMyAssignedProjectsPresented: boolean;

  public sharedFilter: string;
  public sharedFilterEnabled: boolean;

  public _currentSelectedItem: ProjectHeadVO | ProjectGroupVO;
  newConfigName: string = '';

  public scrollToSelected: Subject<void> = new Subject<void>();
  public scrollToTop: Subject<void> = new Subject<void>();
  public currentSelectedItemObserver: Subject<any> = new Subject();

  public initialTab: ProjectNavigationTab;

  public allSpecialGroups: ProjectGroupVO[] = [];
  public visibleSpecialGroups: ProjectGroupVO[] = [];

  public canUndo: boolean;
  public canRedo: boolean;

  public xcomSaveAvailable: boolean;

  public addTimestampToProjectName: boolean = true;
  public addTimestampToProjectNameAvailable: boolean;

  // Fields from save block
  public readonly SAVE_MODE_SINGLE_BASE_ARTICLE: string = 'saveBySingleBaseArticle';
  public readonly SAVE_MODE_MULTIPLE_BASE_ARTICLES: string = 'saveByMultipleBaseArticles';
  public readonly SAVE_AS_BUILDING_BLOCK: string = 'saveAsBuildingBlock';

  public saveMode: string = this.SAVE_MODE_MULTIPLE_BASE_ARTICLES;

  public nameError: boolean = false;

  public compositePartSelectionAllowed: boolean = true;
  public compositePartModificationAllowed: boolean = true;
  public compositeArticleStickTogether: boolean = true;
  public compositeArticleIsInternal: boolean = false;
  public compositeArticleInitiallyRotated: boolean = false;

  public selectedBaseArticleUsageId: string;
  public baseArticleId: number;
  public baseArticleVersion: number;
  public compositeArticleId: number;
  public saveFormIsValid: boolean;

  public viewItems: (ProjectGroupVO | ProjectHeadVO)[];

  public get currentViewedProjectGroup(): ProjectGroupVO | undefined {
    return this._currentViewedProjectGroup;
  }

  public clearCurrentViewedProjectGroup() {
    this._currentViewedProjectGroup = null
  }

  public setCurrentViewedProjectGroup(group: ProjectGroupVO) {
    if(group.id == -1) {
      this._currentViewedProjectGroup = null;
      return;
    }
    if(this._currentProjectFolder) {
      this._currentViewedProjectGroup = this._currentProjectFolder.groups.find(pr => pr.id == group.id);
    } else {
      this._currentViewedProjectGroup = group;
    }
  }

  public get currentProjectFolder(): ProjectGroupVO {
    return this._currentProjectFolder;
  }

  public set currentProjectFolder(value: ProjectGroupVO) {
    this._currentProjectFolder = value;
    this.currentChanged.next(value);
  }

  public get currentSelectedItem(): ProjectHeadVO|ProjectGroupVO {
    return this._currentSelectedItem;
  }

  public set currentSelectedItem(value: ProjectHeadVO|ProjectGroupVO | undefined) {
    this._currentSelectedItem = value;
    this.currentSelectedItemObserver.next(undefined);
  }

  public selectItem(item: ProjectHeadVO | ProjectGroupVO, projectModel: ProjectModel): void {
    this.currentSelectedItem = item;

    if (
      this.currentModel?.visibleSaveBlock &&
      !this.currentModel?.isMoveOrCopy &&
      item &&
      item instanceof ProjectHeadVO &&
      !projectModel.isNewConfigurationGroup
    ) {
      this.newConfigName = item.name;
    }
  }

  public preSelectItem(item: ProjectHeadVO | ProjectGroupVO): void {
    this.currentSelectedItem = item;
  }

  public setSpecialGroups(groups: ProjectGroupVO[]): void {
    if (groups == null) {
      return;
    }
    this.allSpecialGroups = groups;
    this.visibleSpecialGroups = [...this.allSpecialGroups];
  }

  public getRootProjectGroupToPreselect(preselectedProjectGroups: ProjectGroupVO[], userId: number): ProjectGroupVO {
    if (preselectedProjectGroups[0].groupOwner.userId === userId) {
      const isMyAssignedProjectGroup: boolean = ProjectGroupUtils.isMyAssignedProjectsGroup(preselectedProjectGroups[0].parentGroup);
      if (isMyAssignedProjectGroup) {
        this.currentTab = ProjectNavigationTab.MyAssigned;
        return this.rootProjectGroup.groups.find((group) => group.groupName === ProjectNavigationModel.ROOT_NAME_MY_ASSIGNED);
      } else {
        this.currentTab = ProjectNavigationTab.Own;
        return this.rootProjectGroup.groups.find((group) => group.groupName === ProjectNavigationModel.ROOT_NAME_MY_PROJECTS);
      }
    } else {
      this.currentTab = ProjectNavigationTab.Shared;
      const isDefaultImportProjectGroupType: boolean = ProjectGroupUtils.isDefaultImportProjectGroupType(preselectedProjectGroups[0].groupType);
      if (isDefaultImportProjectGroupType) {
        this.currentFilterItem = preselectedProjectGroups[0];
        return this.rootProjectGroup.groups.find(
          (group) => group.groupName === ProjectNavigationModel.ROOT_NAME_SHARED && group.groups[0].id === preselectedProjectGroups[0].id
        );
      } else {
        this.currentFilterItem = new UserIdName(preselectedProjectGroups[0].groupOwner.userId, preselectedProjectGroups[0].groupOwner.username);
        return this.rootProjectGroup.groups.find((group) => group.groupOwner.userId === preselectedProjectGroups[0].groupOwner.userId);
      }
    }
  }

  public updateSharedFilterEnabled(users: UserVO[], userModel: UserVO): void {
    this.sharedFilterEnabled =
      users.length >= ProjectNavigationModel.MAX_SEEN_SHARED_USERS_AMOUNT || UserUtils.hasIncludeProjectsFromAllUsersPermission(userModel);
    this.updateSharedNameList(users, userModel);
  }

  public updateSharedNameList(users: UserVO[], userModel: UserVO): void {
    this.allSharedUsers = uniqWith(
      users.filter((user: UserVO) => user.id !== userModel.id).map((user: UserVO) => new UserIdName(user.id, user.login)),
      (a: UserIdName, b: UserIdName) => a.id === b.id
    ).sort((a, b) => StringUtils.stringCompare(a.name, b.name));
    this.visibleSharedUsers = [...this.allSharedUsers];
  }

  public attachProjectGroup(projectGroup: ProjectGroupVO, user: UserVO): void {
    const lastFromNavigation: ProjectGroupVO = this.navigationChain[this.navigationChain.length - 1];
    lastFromNavigation.groups.push(projectGroup);
    if (this.searchMode) {
      const group: ProjectGroupVO = ProjectGroupUtils.findProjectGroupInATree([this.rootProjectGroup], lastFromNavigation.id);
      if (group) {
        group.groups.push(projectGroup);
      }
    }
    this.currentProjectFolder = lastFromNavigation;
    this.parentGroupMap.setValue(projectGroup.id, this.currentProjectFolder);
    if (this.currentModel.isMoveOrCopy) {
      this.projectGroupCreatedHandler(projectGroup, user);
    }
  }

  private projectGroupCreatedHandler(newGroup: ProjectGroupVO, user: UserVO): void {
    const parentGroup: ProjectGroupVO = this.findParentGroup(newGroup, user);
    if (parentGroup) {
      const copiedNewGroup = CloneUtils.cloneProjectGroupVO(newGroup);
      parentGroup.groups.push(copiedNewGroup);
      this.parentGroupMap.setValue(copiedNewGroup.id, parentGroup);
    }
  }

  public findParentGroup(projectGroup: ProjectGroupVO, user: UserVO): ProjectGroupVO {
    if (projectGroup.parentGroup) {
      return ProjectGroupUtils.findProjectGroupInATree(this.rootProjectGroup.groups, projectGroup.parentGroup.id);
    }
    if (user.id === projectGroup.groupOwner.userId) {
      return projectGroup.isHidden
        ? this.rootProjectGroup.groups.find((g) => g.groupName === ProjectNavigationModel.ROOT_NAME_HIDDEN)
        : this.rootProjectGroup.groups.find((g) => g.groupName === ProjectNavigationModel.ROOT_NAME_MY_PROJECTS);
    } else {
      return this.rootProjectGroup.groups.find((g) => g.groupOwner.userId === projectGroup.groupOwner.userId);
    }
  }

  public resetSelections(): void {
    this.selectedProjectGroups = new Dictionary<number, ProjectGroupVO>();
    this.selectedProjects = new Dictionary<number, ProjectVO>();
  }

  public updateCurrentFilterItemAvailability(group: ProjectGroupVO): void {
    if (!this.currentFilterItem) {
      return;
    }
    let index: number;
    if (this.currentFilterItem instanceof ProjectGroupVO) {
      index = group.groups.findIndex((group) => group.id === this.currentFilterItem.id);
    } else {
      index = group.groups.findIndex((group) => group.groupOwner.userId === this.currentFilterItem.id);
    }
    if (index === -1) {
      this.currentFilterItem = null;
    }
  }

  public isInRoot() {
    return this.navigationChain ? this.navigationChain.length <= 1 : false;
  }

  public getParentGroup(group: ProjectGroupVO): ProjectGroupVO {
    if (!group) {
      return null;
    }
    if (this.currentTab === ProjectNavigationTab.Pinned) {
      return this.pinnedGroupsParentGroupMap.getValue(group.id);
    }
    if (this.currentTab === ProjectNavigationTab.RecentlyUsed) {
      return this.recentlyUsedGroupsParentGroupMap.getValue(group.id);
    }
    return this.parentGroupMap.getValue(group.id);
  }

  public resetAllData(): void {
    this.canUndo = false;
    this.canRedo = false;

    this.currentFilterItem = null;
    this.clearCurrentViewedProjectGroup();
    this.currentTab = ProjectNavigationTab.Own;

    this.selectedProjectGroups = new Dictionary<number, ProjectGroupVO>();
    this.selectedProjects = new Dictionary<number, ProjectVO>();

    this.allSpecialGroups = [];
    this.visibleSpecialGroups = [];

    this.isMyAssignedProjectsPresented = false;

    this.allSharedUsers = [];
    this.visibleSharedUsers = [];
    this.sharedFilter = ``;
    this.sharedFilterEnabled = false;
  }
}

export enum ProjectNavigationSortingType {
  NAME = 'NAME',
  ARTICLE_NUMBER = 'ARTICLE_NUMBER',
  DATE = 'DATE',
  STATUS = 'STATUS'
}

export interface ProjectNavigationSorting {
  type: ProjectNavigationSortingType;
  ascending: boolean;
}

export class UserIdName {
  constructor(public id: number, public name: string) {
  }
}

export type FilterItem = UserIdName | ProjectGroupVO;

export enum ProjectNavigationTab {
  Own,
  MyAssigned,
  RecentlyUsed,
  Pinned,
  Shared,
  Search
}

export type ProjectNavigationResult<TResult> = {
  success: true,
  result: TResult
} | {
  success: false
}
